import React, { useEffect, useRef } from 'react';
import { Animated, Image, View, Dimensions } from 'react-native';
import { AppImages } from '../../utility/AppImages';

interface AnimatedGiftProps {
  giftImage: any;
  startPosition: { x: number; y: number };
  endPosition: { x: number; y: number };
  playerIndex: number;
  onAnimationComplete: () => void;
  duration?: number;
}

const AnimatedGift: React.FC<AnimatedGiftProps> = ({
  giftImage,
  startPosition,
  endPosition,
  playerIndex,
  onAnimationComplete,
  duration = 1500,
}) => {
  const translateX = useRef(new Animated.Value(startPosition.x)).current;
  const translateY = useRef(new Animated.Value(startPosition.y)).current;
  const scale = useRef(new Animated.Value(1)).current;
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Create a curved animation path
    const animationX = Animated.timing(translateX, {
      toValue: endPosition.x,
      duration,
      useNativeDriver: true,
    });

    const animationY = Animated.timing(translateY, {
      toValue: endPosition.y,
      duration,
      useNativeDriver: true,
    });

    // Scale animation - starts normal, gets bigger in middle, then smaller at end
    const scaleAnimation = Animated.sequence([
      Animated.timing(scale, {
        toValue: 1.5,
        duration: duration * 0.3,
        useNativeDriver: true,
      }),
      Animated.timing(scale, {
        toValue: 0.8,
        duration: duration * 0.7,
        useNativeDriver: true,
      }),
    ]);

    // Fade out at the end
    const fadeAnimation = Animated.sequence([
      Animated.delay(duration * 0.8),
      Animated.timing(opacity, {
        toValue: 0,
        duration: duration * 0.2,
        useNativeDriver: true,
      }),
    ]);

    // Run all animations in parallel
    Animated.parallel([
      animationX,
      animationY,
      scaleAnimation,
      fadeAnimation,
    ]).start(() => {
      onAnimationComplete();
    });
  }, []);

  return (
    <Animated.View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        transform: [
          { translateX },
          { translateY },
          { scale },
        ],
        opacity,
      }}
    >
      <View
        style={{
          backgroundColor: playerIndex === 0 ? 'rgba(0, 150, 255, 0.3)' : // Blue for Player 1
                          playerIndex === 1 ? 'rgba(255, 100, 0, 0.3)' :  // Orange for Player 2  
                          'rgba(109, 8, 195, 0.3)', // Purple for Both
          borderRadius: 25,
          padding: 8,
          shadowColor: playerIndex === 0 ? '#0096FF' : // Blue shadow
                      playerIndex === 1 ? '#FF6400' :  // Orange shadow
                      '#6D08C3', // Purple shadow
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 0.9,
          shadowRadius: 12,
          elevation: 10,
          borderWidth: 2,
          borderColor: playerIndex === 0 ? '#0096FF' : // Blue border
                      playerIndex === 1 ? '#FF6400' :  // Orange border
                      '#6D08C3', // Purple border
        }}
      >
        <Image
          source={giftImage || AppImages.icWhiteDollarIcon}
          style={{
            width: 35,
            height: 35,
          }}
          resizeMode="contain"
        />
      </View>
    </Animated.View>
  );
};

export default AnimatedGift;
